import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CustomerService } from '../../../Services/customer.service';
import { Customer } from '../../../Models/customer.model';
import { CustomerFormComponent } from '../customer-form/customer-form.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { MaintenanceStatus } from '../../../Models/maintenance.model';
import { AuthService } from '../../../api-authorization/authorize.service';
import { EinsatzAnsichtStateService } from '../../../Services/einsatz-ansicht-state.service';

type CustomerFilter = 'all' | 'attention' | 'dueSoon' | 'withoutEquipment';
type CustomerSort = 'attention' | 'name' | 'nextDate' | 'active';

@Component({
  selector: 'app-customer-list',
  templateUrl: './customer-list.component.html',
  styleUrls: ['./customer-list.component.css']
})
export class CustomerListComponent implements OnInit {
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  searchTerm = '';
  loading = true;
  customerFilter: CustomerFilter = 'all';
  sortMode: CustomerSort = 'attention';

  readonly customerFilters: { key: CustomerFilter; label: string; icon: string }[] = [
    { key: 'all', label: 'Alle', icon: 'groups' },
    { key: 'attention', label: 'Handlungsbedarf', icon: 'priority_high' },
    { key: 'dueSoon', label: 'Bald fällig', icon: 'event' },
    { key: 'withoutEquipment', label: 'Ohne Anlagen', icon: 'electrical_services' }
  ];

  totalEquipment = 0;
  totalActive = 0;
  totalOverdue = 0;

  private readonly stateKey = 'wartung-customer-list-state';

  constructor(
    private customerService: CustomerService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private authService: AuthService,
    private einsatzAnsichtState: EinsatzAnsichtStateService
  ) {}

  /** Darf der aktuelle Benutzer Kunden anlegen/bearbeiten/löschen? (Montage = nur lesen) */
  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  ngOnInit(): void {
    // Wer bis zur Kundenliste zurückgeht, fängt beim nächsten Kunden von vorne
    // an: Personenfilter und Scrollposition der Einsatzliste gelten nur für den
    // Ausflug in einen einzelnen Einsatz und zurück.
    this.einsatzAnsichtState.alleVerwerfen();

    this.restoreState();
    this.loadCustomers();
  }

  loadCustomers(): void {
    this.loading = true;
    this.customerService.getCustomers().subscribe({
      next: (customers) => {
        this.customers = customers ?? [];
        this.calcStats();
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Fehler beim Laden der Kunden', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.filteredCustomers = this.customers
      .filter(c => this.matchesSearch(c, term))
      .filter(c => this.matchesFilter(c))
      .sort((a, b) => this.compareCustomers(a, b));
    this.persistState();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilter();
  }

  /** Enter in der Suche: obersten Treffer direkt öffnen. */
  openFirstResult(): void {
    if (this.filteredCustomers.length > 0) {
      this.openCustomer(this.filteredCustomers[0]);
    }
  }

  /** Ist gerade eine Suche/Filter/Sortierung abweichend vom Standard aktiv? */
  get hasActiveFilters(): boolean {
    return !!this.searchTerm.trim() || this.customerFilter !== 'all' || this.sortMode !== 'attention';
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.customerFilter = 'all';
    this.sortMode = 'attention';
    this.applyFilter();
  }

  /** Kundenseite öffnen und dort direkt Wartung/Einsatz anlegen. */
  openCustomerWithAction(customer: Customer, action: 'plan' | 'service', event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/wartung/kunden', customer.id], { queryParams: { action } });
  }

  private restoreState(): void {
    try {
      const raw = sessionStorage.getItem(this.stateKey);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (typeof s.searchTerm === 'string') this.searchTerm = s.searchTerm;
      if (s.customerFilter) this.customerFilter = s.customerFilter;
      if (s.sortMode) this.sortMode = s.sortMode;
    } catch { /* ignore */ }
  }

  private persistState(): void {
    try {
      sessionStorage.setItem(this.stateKey, JSON.stringify({
        searchTerm: this.searchTerm,
        customerFilter: this.customerFilter,
        sortMode: this.sortMode
      }));
    } catch { /* ignore */ }
  }

  setCustomerFilter(filter: CustomerFilter): void {
    this.customerFilter = filter;
    this.applyFilter();
  }

  setSortMode(sortMode: CustomerSort): void {
    this.sortMode = sortMode;
    this.applyFilter();
  }

  private calcStats(): void {
    this.totalEquipment = this.customers.reduce((s, c) => s + (c.equipments?.length ?? 0), 0);
    const allM = this.customers.flatMap(c => (c.equipments ?? []).flatMap(e => e.maintenances ?? []));
    this.totalActive = allM.filter(m => this.isOpenMaintenance(m)).length;
    this.totalOverdue = allM.filter(m => this.isMaintenanceOverdue(m)).length;
  }

  openCustomerForm(customer?: Customer): void {
    const dialogRef = this.dialog.open(CustomerFormComponent, {
      width: '880px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: customer ? { ...customer } : null,
      autoFocus: true
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadCustomers();
        this.snackBar.open(customer ? 'Kunde aktualisiert' : 'Kunde angelegt', 'OK', { duration: 3000 });
      }
    });
  }

  deleteCustomer(customer: Customer, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Kunde löschen', message: `"${customer.name}" wirklich löschen? Alle zugehörigen Daten werden entfernt.` }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.customerService.deleteCustomer(customer.id).subscribe({
          next: () => {
            this.loadCustomers();
            this.snackBar.open('Kunde gelöscht', 'OK', { duration: 3000 });
          },
          // Der Server sagt bei einem Konflikt (409), WAS den Kunden noch haelt -
          // etwa offene Projekte. Diese Auskunft weiterzugeben ist der ganze
          // Unterschied zwischen "geht nicht" und "das ist zu tun".
          error: (fehler) => {
            const meldung = fehler?.error?.message;
            this.snackBar.open(
              typeof meldung === 'string' && meldung.length > 0 ? meldung : 'Fehler beim Löschen',
              'OK',
              { duration: typeof meldung === 'string' && meldung.length > 0 ? 8000 : 4000 }
            );
          }
        });
      }
    });
  }

  openCustomer(customer: Customer): void {
    this.router.navigate(['/wartung/kunden', customer.id]);
  }

  editCustomer(customer: Customer, event: Event): void {
    event.stopPropagation();
    this.openCustomerForm(customer);
  }

  getEquipmentCount(c: Customer): number {
    return c.equipments?.length ?? 0;
  }

  getActiveCount(c: Customer): number {
    return (c.equipments ?? [])
      .flatMap(e => e.maintenances ?? [])
      .filter(m => this.isOpenMaintenance(m))
      .length;
  }

  getOverdueCount(c: Customer): number {
    return (c.equipments ?? [])
      .flatMap(e => e.maintenances ?? [])
      .filter(m => this.isMaintenanceOverdue(m))
      .length;
  }

  getDueSoonCount(c: Customer): number {
    return (c.equipments ?? [])
      .flatMap(e => e.maintenances ?? [])
      .filter(m => this.isOpenMaintenance(m))
      .filter(m => {
        const date = this.getTargetDate(m);
        const days = this.getDaysUntil(date);
        return days >= 0 && days <= 7;
      })
      .length;
  }

  getNextMaintenance(c: Customer): Date | null {
    const dates = (c.equipments ?? [])
      .flatMap(e => e.maintenances ?? [])
      .filter(m => this.isOpenMaintenance(m))
      .map(m => this.getTargetDate(m))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    return dates[0] ?? null;
  }

  getCustomerStatus(c: Customer): 'ok' | 'warn' | 'danger' {
    if (this.getOverdueCount(c) > 0) return 'danger';
    if (this.getEquipmentCount(c) === 0) return 'warn';
    const next = this.getNextMaintenance(c);
    if (next) {
      const days = this.getDaysUntil(next);
      if (days <= 7 && days >= 0) return 'warn';
    }
    return 'ok';
  }

  getCustomerStatusLabel(c: Customer): string {
    const overdue = this.getOverdueCount(c);
    if (overdue > 0) return `${overdue} überfällig`;
    if (this.getEquipmentCount(c) === 0) return 'Setup offen';
    const dueSoon = this.getDueSoonCount(c);
    if (dueSoon > 0) return `${dueSoon} bald fällig`;
    return 'OK';
  }

  getNextMaintenanceLabel(c: Customer): string {
    const next = this.getNextMaintenance(c);
    if (!next) return 'Kein Termin';
    const days = this.getDaysUntil(next);
    if (days < -1) return `Seit ${Math.abs(days)} Tagen`;
    if (days === -1) return 'Seit gestern';
    if (days === 0) return 'Heute';
    if (days === 1) return 'Morgen';
    return next.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  getEquipmentPreview(c: Customer): string {
    const equipment = c.equipments ?? [];
    if (equipment.length === 0) return 'Noch keine Anlagen angelegt';
    const names = equipment.map(e => e.name).filter(Boolean).slice(0, 3);
    const rest = equipment.length - names.length;
    return rest > 0 ? `${names.join(', ')} +${rest}` : names.join(', ');
  }

  getPrimaryContact(c: Customer): string {
    if (c.contactPersonName) return c.contactPersonName;
    if (c.contactPersonPhone) return c.contactPersonPhone;
    if (c.contactPersonEmail) return c.contactPersonEmail;
    return 'Kein Kontakt hinterlegt';
  }

  getFilterCount(filter: CustomerFilter): number {
    return this.customers.filter(c => this.matchesFilter(c, filter)).length;
  }

  getLogoUrl(logoPath: string): string {
    return this.customerService.getLogoUrl(logoPath);
  }

  trackByCustomerId(_: number, customer: Customer): number {
    return customer.id;
  }

  private matchesSearch(customer: Customer, term: string): boolean {
    if (!term) return true;
    const equipmentText = (customer.equipments ?? [])
      .map(e => `${e.name} ${e.location ?? ''}`)
      .join(' ');
    return `${customer.name} ${customer.address ?? ''} ${customer.contactPersonName ?? ''} ${customer.contactPersonEmail ?? ''} ${customer.contactPersonPhone ?? ''} ${equipmentText}`
      .toLowerCase()
      .includes(term);
  }

  private matchesFilter(customer: Customer, filter: CustomerFilter = this.customerFilter): boolean {
    switch (filter) {
      case 'attention':
        return this.getOverdueCount(customer) > 0;
      case 'dueSoon':
        return this.getOverdueCount(customer) === 0 && this.getDueSoonCount(customer) > 0;
      case 'withoutEquipment':
        return this.getEquipmentCount(customer) === 0;
      case 'all':
      default:
        return true;
    }
  }

  private compareCustomers(a: Customer, b: Customer): number {
    switch (this.sortMode) {
      case 'name':
        return a.name.localeCompare(b.name, 'de');
      case 'nextDate': {
        const aDate = this.getNextMaintenance(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bDate = this.getNextMaintenance(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (aDate !== bDate) return aDate - bDate;
        return a.name.localeCompare(b.name, 'de');
      }
      case 'active': {
        const activeDiff = this.getActiveCount(b) - this.getActiveCount(a);
        if (activeDiff !== 0) return activeDiff;
        return a.name.localeCompare(b.name, 'de');
      }
      case 'attention':
      default: {
        const rankDiff = this.getCustomerRank(a) - this.getCustomerRank(b);
        if (rankDiff !== 0) return rankDiff;
        const aDate = this.getNextMaintenance(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bDate = this.getNextMaintenance(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (aDate !== bDate) return aDate - bDate;
        return a.name.localeCompare(b.name, 'de');
      }
    }
  }

  private getCustomerRank(customer: Customer): number {
    if (this.getOverdueCount(customer) > 0) return 0;
    if (this.getDueSoonCount(customer) > 0) return 1;
    if (this.getEquipmentCount(customer) === 0) return 2;
    return 3;
  }

  private isOpenMaintenance(m: { status: MaintenanceStatus }): boolean {
    return m.status !== MaintenanceStatus.Completed && m.status !== MaintenanceStatus.Cancelled;
  }

  private isMaintenanceOverdue(m: { status: MaintenanceStatus; dueDate?: string | Date; nextScheduledDate?: string | Date; scheduledDate: string | Date }): boolean {
    if (!this.isOpenMaintenance(m)) return false;
    if (m.status === MaintenanceStatus.Overdue) return true;
    return this.getDaysUntil(this.getTargetDate(m)) < 0;
  }

  private getTargetDate(m: { dueDate?: string | Date; nextScheduledDate?: string | Date; scheduledDate: string | Date }): Date {
    return new Date(m.dueDate ?? m.nextScheduledDate ?? m.scheduledDate);
  }

  private getDaysUntil(date: Date): number {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    return Math.round((target - startOfToday) / 86400000);
  }
}
